import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { TemplateText, Text } from "src/presentation/components/atoms";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import ReceptividadIcon from "@assets/icons/Valores - receptividad.svg";
import EquipoIcon from "@assets/icons/valores - trabajo equipo.svg";
import PerserveranciaIcon from "@assets/icons/valores - perseverancia.svg";
import EficienciaIcon from "@assets/icons/valores - eficiencia.svg";
import SostenibilidadIcon from "@assets/icons/valores - sostenibilidad.svg";
import InnovacionIcon from "@assets/icons/valores - innovacion.svg";
import OrientacionIcon from "@assets/icons/valores - orientacion agricultor.svg";
import CompromisoIcon from "@assets/icons/valores - compromiso.svg";
const PlaceholderImage = "/static/images/quienes-placeholder.jpg";
import styles from "./equipo-valores.module.scss";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { settings } from "@config/settings";
import { MainRouter } from "@config/urls";
import { CountryLocationService } from "@services/country-location-service";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { localesParserDictionary } from "@utils/locales-parser";

const data = [
  { key: "direccion", logo: <OrientacionIcon />, img: PlaceholderImage },
  { key: "desarrollo", logo: <InnovacionIcon />, img: PlaceholderImage },
  { key: "operaciones", logo: <SostenibilidadIcon />, img: PlaceholderImage },
  { key: "recursoshumanos", logo: <EficienciaIcon />, img: PlaceholderImage },
  {
    key: "administracion",
    logo: <PerserveranciaIcon />,
    img: PlaceholderImage,
  },
  {
    key: "marketing",
    logo: <EquipoIcon />,
    img: PlaceholderImage,
  },
  {
    key: "comercial",
    logo: <ReceptividadIcon />,
    img: PlaceholderImage,
  },
  {
    key: "finanzas",
    logo: <CompromisoIcon />,
    img: PlaceholderImage,
  },
];

const Home: NextPage = () => {
  const { t } = useTranslation();
  const TopBanner = () => {
    return (
      <section
        className={styles.topbanner}
        style={{
          backgroundImage: `url(${"/static/images/nosotros-equipo-valores-manvert-bioestimulantes.jpg"})`,
        }}
      >
        <div className={styles.card}>
          <Text as="h1">{t("EquipoValoresView.topsection.title")}</Text>
          <TemplateText template="normal">
            {t("EquipoValoresView.topsection.description")}
          </TemplateText>
        </div>
      </section>
    );
  };
  const Cards = () => {
    return (
      <div className={styles.valoresContainer}>
        {data.map((item) => {
          return (
            <div key={item.key} className={styles.card}>
              {item.logo}
              <Text as="h4">
                {t(`EquipoValoresView.sections.${item.key}.title`)}
              </Text>
              <TemplateText template="small">
                {t(`EquipoValoresView.sections.${item.key}.description`)}
              </TemplateText>
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <>
      <NextSeo
        title={t("SEOMeta.EquipoValoresView.title") || ""}
        description={t("SEOMeta.EquipoValoresView.description") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.nosotros"),
            url: MainRouter.nosotros.generatePath(),
          },
          {
            label: t("urls.equipo"),
            url: MainRouter["biovert-mundo"].generatePath(),
          },
        ]}
      >
        <div className={styles.root}>
          <TopBanner />
          <Cards />
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, res } = Props;

  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: selectedCountry.countrycode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productosDestacados: true,
    productosGenericos: true,
  });

  return {
    props: {
      country: selectedCountry.countrycode,
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default Home;
